'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Trash2, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useHelpStore } from '@/lib/help/store';
import { sendChatMessage } from '@/lib/help/api';
import { getDocInfoForRoute } from '@/lib/help/routes';
import { ChatMessage } from './ChatMessage';

const SUGGESTED_QUESTIONS = [
  'How do I create a new agent?',
  'What are integrations used for?',
  'How do I connect a data source?',
  'What are MCP tools?',
];

export function ChatTab() {
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const messages = useHelpStore((s) => s.messages);
  const isLoading = useHelpStore((s) => s.isLoading);
  const sessionId = useHelpStore((s) => s.sessionId);
  const currentRoute = useHelpStore((s) => s.currentRoute);
  const addMessage = useHelpStore((s) => s.addMessage);
  const updateMessage = useHelpStore((s) => s.updateMessage);
  const appendToMessage = useHelpStore((s) => s.appendToMessage);
  const setLoading = useHelpStore((s) => s.setLoading);
  const clearMessages = useHelpStore((s) => s.clearMessages);

  const docInfo = getDocInfoForRoute(currentRoute);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = useCallback(async (messageText?: string) => {
    const text = (messageText || input).trim();
    if (!text || isLoading) return;

    setInput('');

    // Add user message
    addMessage({
      role: 'user',
      content: text,
    });

    // Add placeholder for assistant message
    const assistantMsgId = addMessage({
      role: 'assistant',
      content: '',
      isStreaming: true,
    });

    setLoading(true);

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      let sources: typeof messages[0]['sources'] = [];

      await sendChatMessage(
        text,
        sessionId,
        currentRoute,
        (event) => {
          switch (event.type) {
            case 'token':
              if (event.content) {
                appendToMessage(assistantMsgId, event.content);
              }
              break;
            case 'sources':
              if (event.sources) {
                sources = event.sources;
              }
              break;
            case 'done':
              updateMessage(assistantMsgId, {
                isStreaming: false,
                sources,
              });
              break;
            case 'error':
              updateMessage(assistantMsgId, {
                content: `Sorry, I encountered an error: ${event.error || 'Unknown error'}`,
                isStreaming: false,
              });
              break;
          }
        },
        abortControllerRef.current.signal
      );
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        updateMessage(assistantMsgId, {
          content: 'Sorry, I encountered an error processing your request. Please try again.',
          isStreaming: false,
        });
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [input, isLoading, sessionId, currentRoute, addMessage, appendToMessage, updateMessage, setLoading]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    handleSend(question);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Context indicator */}
      <div className="px-4 py-2 border-b bg-muted/30">
        <p className="text-xs text-muted-foreground">
          <Sparkles className="inline h-3 w-3 mr-1" />
          Context: <span className="font-medium">{docInfo.title}</span>
        </p>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1" ref={scrollRef}>
        <div className="p-4 space-y-4">
          {messages.length === 0 ? (
            <EmptyState
              onSelectQuestion={handleSuggestedQuestion}
              isLoading={isLoading}
            />
          ) : (
            messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Input area */}
      <div className="border-t p-4 bg-card">
        <div className="flex gap-2">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about this page or platform features..."
            className="min-h-[44px] max-h-32 resize-none"
            disabled={isLoading}
            rows={1}
          />
          <div className="flex flex-col gap-1">
            <Button
              size="icon"
              onClick={() => handleSend()}
              disabled={!input.trim() || isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
            {messages.length > 0 && (
              <Button
                size="icon"
                variant="ghost"
                onClick={clearMessages}
                disabled={isLoading}
                title="Clear chat"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}

function EmptyState({
  onSelectQuestion,
  isLoading,
}: {
  onSelectQuestion: (question: string) => void;
  isLoading: boolean;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
        <Sparkles className="h-6 w-6 text-primary" />
      </div>
      <h3 className="font-semibold mb-2">Ask Anything</h3>
      <p className="text-sm text-muted-foreground mb-6 max-w-xs">
        I can help you understand features, guide you through tasks, and answer
        questions about the Matricks platform.
      </p>

      <div className="w-full space-y-2">
        <p className="text-xs font-medium text-muted-foreground">
          Suggested questions:
        </p>
        {SUGGESTED_QUESTIONS.map((question) => (
          <button
            key={question}
            onClick={() => onSelectQuestion(question)}
            disabled={isLoading}
            className="w-full text-left px-3 py-2 rounded-lg border bg-card hover:bg-accent text-sm transition-colors disabled:opacity-50"
          >
            {question}
          </button>
        ))}
      </div>
    </div>
  );
}
